import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ImagePlus, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { resizeToJpeg } from "./topShopShared";

export const CHALLENGE_PHOTO_BUCKET = "top-shop-challenge-photos";
export const MAX_CHALLENGE_PHOTOS = 10;
export const MAX_CHALLENGE_LINKS = 10;
const EVIDENCE_KEY = ["top-shop", "challenge-evidence"] as const;

export interface EvidenceItem {
  id: string;
  kind: "photo" | "link";
  storage_path: string | null;
  url: string | null;
  caption: string | null;
}

/** For a Top Shop challenge assignment: the focus area and the signed-in user's accepted or completed application. */
export function useTopShopChallengeContext(assignmentId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["top-shop", "challenge-context", assignmentId, user?.id],
    enabled: !!assignmentId && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: focusAreaId } = await supabase.rpc("top_shop_focus_area_for_assignment", { p_assignment_id: assignmentId! });
      if (!focusAreaId) return null;
      const { data: area } = await supabase.from("program_focus_areas").select("program_id").eq("id", focusAreaId as string).maybeSingle();
      if (!area) return { focusAreaId: focusAreaId as string, applicationId: null };
      const { data: app } = await supabase.from("program_applications").select("id, status")
        .eq("program_id", area.program_id).eq("user_id", user!.id).maybeSingle();
      const ok = app && (app.status === "accepted" || app.status === "completed");
      return { focusAreaId: focusAreaId as string, applicationId: ok ? (app!.id as string) : null };
    },
  });
}

/** Evidence rows for one application and challenge. */
export function useChallengeEvidence(applicationId: string | null | undefined, focusAreaId: string | null | undefined) {
  return useQuery({
    queryKey: [...EVIDENCE_KEY, applicationId, focusAreaId],
    enabled: !!applicationId && !!focusAreaId,
    queryFn: async () => {
      const { data, error } = await supabase.from("program_challenge_evidence")
        .select("id, kind, storage_path, url, caption")
        .eq("application_id", applicationId!).eq("focus_area_id", focusAreaId!)
        .order("sort_order").order("created_at");
      if (error) throw error;
      return (data ?? []) as EvidenceItem[];
    },
  });
}

/** Photo and link counts per focus area for an application. */
export function useEvidenceCounts(applicationId: string | null | undefined) {
  return useQuery({
    queryKey: [...EVIDENCE_KEY, "counts", applicationId],
    enabled: !!applicationId,
    queryFn: async () => {
      const { data, error } = await supabase.from("program_challenge_evidence")
        .select("focus_area_id, kind").eq("application_id", applicationId!);
      if (error) throw error;
      const out: Record<string, { photos: number; links: number }> = {};
      (data ?? []).forEach((r) => {
        const c = (out[r.focus_area_id] ??= { photos: 0, links: 0 });
        if (r.kind === "photo") c.photos++; else c.links++;
      });
      return out;
    },
  });
}

export function evidenceCountLabel(c?: { photos: number; links: number }) {
  const p = c?.photos ?? 0, l = c?.links ?? 0;
  return `${p} ${p === 1 ? "photo" : "photos"}, ${l} ${l === 1 ? "link" : "links"}`;
}

function useSignedUrls(items: EvidenceItem[]) {
  const paths = items.filter((i) => i.kind === "photo" && i.storage_path).map((i) => i.storage_path!) ;
  return useQuery({
    queryKey: ["top-shop", "challenge-photo-urls", paths],
    enabled: paths.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.storage.from(CHALLENGE_PHOTO_BUCKET).createSignedUrls(paths, 3600);
      const map: Record<string, string> = {};
      (data ?? []).forEach((d, i) => { if (d.signedUrl) map[paths[i]] = d.signedUrl; });
      return map;
    },
  });
}

/** Read-only photo grid and link list. */
export function EvidenceViewer({ items }: { items: EvidenceItem[] }) {
  const { data: urls = {} } = useSignedUrls(items);
  const [full, setFull] = useState<string | null>(null);
  const photos = items.filter((i) => i.kind === "photo");
  const links = items.filter((i) => i.kind === "link");
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No photos or links were added.</p>;
  return (
    <div className="space-y-4">
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {photos.map((p) => (
            <figure key={p.id} className="space-y-1">
              <button type="button" className="aspect-square w-full overflow-hidden rounded-md border bg-muted"
                onClick={() => urls[p.storage_path!] && setFull(urls[p.storage_path!])} aria-label="View photo full size">
                {urls[p.storage_path!] && <img src={urls[p.storage_path!]} alt={p.caption ?? "Challenge photo"} className="h-full w-full object-cover" />}
              </button>
              {p.caption && <figcaption className="text-xs text-muted-foreground break-words">{p.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
      {links.length > 0 && (
        <ul className="space-y-2">
          {links.map((l) => (
            <li key={l.id}>
              <a href={l.url!} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 text-sm text-primary hover:underline break-all">
                <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />{l.url}
              </a>
              {l.caption && <p className="ml-6 text-xs text-muted-foreground break-words">{l.caption}</p>}
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!full} onOpenChange={(o) => !o && setFull(null)}>
        <DialogContent className="max-w-4xl">
          <DialogTitle className="sr-only">Challenge photo</DialogTitle>
          {full && <img src={full} alt="Challenge photo full size" className="h-auto max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CaptionInput({ item, onSaved }: { item: EvidenceItem; onSaved: () => void }) {
  const [value, setValue] = useState(item.caption ?? "");
  useEffect(() => setValue(item.caption ?? ""), [item.caption]);
  const save = async () => {
    const next = value.trim() || null;
    if (next === (item.caption ?? null)) return;
    const { error } = await supabase.from("program_challenge_evidence").update({ caption: next }).eq("id", item.id);
    if (error) toast.error(error.message); else onSaved();
  };
  return (
    <Input value={value} maxLength={300} placeholder="Caption (optional)" aria-label="Caption"
      onChange={(e) => setValue(e.target.value)} onBlur={save} className="h-8 text-xs" />
  );
}

/** Participant editor: add photos and links, which save immediately. */
export function EvidenceEditor({ applicationId, focusAreaId, items }: { applicationId: string; focusAreaId: string; items: EvidenceItem[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [adding, setAdding] = useState(false);
  const { data: urls = {} } = useSignedUrls(items);
  const photos = items.filter((i) => i.kind === "photo");
  const links = items.filter((i) => i.kind === "link");
  const refresh = () => qc.invalidateQueries({ queryKey: EVIDENCE_KEY });

  const addPhotos = async (files: FileList | null) => {
    if (!files || !user) return;
    const room = MAX_CHALLENGE_PHOTOS - photos.length;
    const list = Array.from(files).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (list.length < files.length) toast.error("Only JPEG, PNG or WebP photos can be added.");
    if (list.length > room) toast.error(`You can add ${room} more ${room === 1 ? "photo" : "photos"}.`);
    setUploading(true);
    for (const file of list.slice(0, Math.max(room, 0))) {
      try {
        const blob = await resizeToJpeg(file);
        const path = `${user.id}/${applicationId}/${focusAreaId}/${crypto.randomUUID()}.jpg`;
        const { error: upErr } = await supabase.storage.from(CHALLENGE_PHOTO_BUCKET).upload(path, blob, { contentType: "image/jpeg" });
        if (upErr) throw upErr;
        const { error } = await supabase.from("program_challenge_evidence").insert({
          application_id: applicationId, focus_area_id: focusAreaId, user_id: user.id, kind: "photo", storage_path: path, sort_order: Date.now() % 1000000,
        });
        if (error) { await supabase.storage.from(CHALLENGE_PHOTO_BUCKET).remove([path]); throw error; }
      } catch (e: any) {
        toast.error(`Could not add ${file.name}: ${e.message ?? "upload failed"}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    refresh();
  };

  const addLink = async () => {
    const u = url.trim();
    if (!/^https:\/\/\S+$/i.test(u) || u.length > 2000) { toast.error("Links must start with https://"); return; }
    if (!user) return;
    setAdding(true);
    const { error } = await supabase.from("program_challenge_evidence").insert({
      application_id: applicationId, focus_area_id: focusAreaId, user_id: user.id, kind: "link", url: u, caption: caption.trim() || null, sort_order: Date.now() % 1000000,
    });
    setAdding(false);
    if (error) { toast.error(error.message); return; }
    setUrl(""); setCaption("");
    refresh();
  };

  const remove = async (item: EvidenceItem) => {
    const { error } = await supabase.from("program_challenge_evidence").delete().eq("id", item.id);
    if (error) { toast.error(error.message); return; }
    if (item.storage_path) await supabase.storage.from(CHALLENGE_PHOTO_BUCKET).remove([item.storage_path]);
    refresh();
  };

  return (
    <div className="space-y-5 rounded-lg border p-4">
      <div>
        <h4 className="font-semibold">Photos and links</h4>
        <p className="text-sm text-muted-foreground">Saved. You can keep adding photos, links and answers until you mark this challenge ready for scoring.</p>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>Photos</Label>
          <span className="text-xs text-muted-foreground">{photos.length} of {MAX_CHALLENGE_PHOTOS} photos</span>
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {photos.map((p) => (
              <div key={p.id} className="space-y-1">
                <div className="relative aspect-square overflow-hidden rounded-md border bg-muted">
                  {urls[p.storage_path!] && <img src={urls[p.storage_path!]} alt={p.caption ?? "Challenge photo"} className="h-full w-full object-cover" />}
                  <Button type="button" size="icon" variant="secondary" className="absolute right-1 top-1 h-7 w-7" onClick={() => remove(p)} aria-label="Remove photo">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <CaptionInput item={p} onSaved={refresh} />
              </div>
            ))}
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} />
        <Button type="button" variant="outline" disabled={uploading || photos.length >= MAX_CHALLENGE_PHOTOS} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}Add photos
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor={`ev-url-${focusAreaId}`}>Add a link</Label>
          <span className="text-xs text-muted-foreground">{links.length} of {MAX_CHALLENGE_LINKS} links</span>
        </div>
        {links.length > 0 && (
          <ul className="space-y-2">
            {links.map((l) => (
              <li key={l.id} className="flex items-start gap-2 rounded-md border p-2">
                <Link2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1 space-y-1">
                  <a href={l.url!} target="_blank" rel="noopener noreferrer" className="block text-sm text-primary hover:underline break-all">{l.url}</a>
                  <CaptionInput item={l} onSaved={refresh} />
                </div>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(l)} aria-label="Remove link">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {links.length < MAX_CHALLENGE_LINKS && (
          <div className="space-y-2">
            <Input id={`ev-url-${focusAreaId}`} type="url" inputMode="url" placeholder="https://" value={url} maxLength={2000} onChange={(e) => setUrl(e.target.value)} />
            <Input placeholder="Caption (optional)" value={caption} maxLength={300} onChange={(e) => setCaption(e.target.value)} aria-label="Link caption" />
            <p className="text-xs text-muted-foreground">Add a link to a video or page, such as YouTube, Google Drive or Facebook. Make sure anyone with the link can view it.</p>
            <Button type="button" variant="outline" disabled={adding || !url.trim()} onClick={addLink}>
              {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}Add link
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
